import { forwardRef, useMemo } from 'react'
import type { RiskAssessmentSectionRecord } from '@artem-lex/compliance-documents'
import type { Editor } from '@tiptap/core'
import { useTranslation } from 'react-i18next'
import { ComplianceDocumentView } from '../../../shared/compliance-document/ComplianceDocumentView'
import type { ComplianceDocumentViewHandle } from '../../../shared/compliance-document/complianceDocumentViewHandle'
import { ComplianceDocumentStreamingPanel } from '../../../shared/compliance-document/ComplianceDocumentStreamingPanel'
import { shouldShowRiskAssessmentSectionEditor } from './riskAssessmentSectionUi'
import { getSectionHeadingLabel } from '../../../shared/compliance-document/complianceDocumentEditorUtils'
import { useComplianceDocumentManifestContext } from '../../../shared/compliance-document/ComplianceDocumentManifestContext'

type RiskAssessmentDocumentViewProps = {
  complianceId: string
  sections: RiskAssessmentSectionRecord[]
  onSectionsChange: (sections: RiskAssessmentSectionRecord[]) => void
  onAutosave: (sections: RiskAssessmentSectionRecord[]) => Promise<void>
  onCreateVersion: (sections: RiskAssessmentSectionRecord[]) => Promise<void>
  isAutosaving: boolean
  isCreatingVersion: boolean
  lastSavedAt: Date | null
  isGenerating: boolean
  onExportPdf: () => void | Promise<void>
  isExportPending: boolean
  generateMenu: React.ReactNode
  onRegenerateSection: (sectionId: string) => void
  contentSyncKey: number
  streamingSectionId: string | null
  streamingContent: string
  onFocusedEditorChange?: (editor: Editor | null, sectionId: string | null) => void
  bubbleMenuContent?: React.ReactNode
  bubbleMenuHidden?: boolean
  /** When true, editor blur does not schedule autosave (rewrite drawer is open). */
  rewritePanelOpen?: boolean
}

export const RiskAssessmentDocumentView = forwardRef<
  ComplianceDocumentViewHandle,
  RiskAssessmentDocumentViewProps
>(function RiskAssessmentDocumentView(props, ref) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { resolveSectionDisplayTitle } = useComplianceDocumentManifestContext()

  const streamingStatusLabel = useMemo(() => {
    if (props.streamingSectionId == null) {
      return t('riskAssessmentPage.generating')
    }
    const streamingSection = props.sections.find(
      (section) => section.sectionId === props.streamingSectionId
    )
    const sectionLabel =
      streamingSection != null
        ? getSectionHeadingLabel(
            streamingSection,
            resolveSectionDisplayTitle(streamingSection)
          )
        : props.streamingSectionId
    return t('riskAssessmentPage.generatingSection', { section: sectionLabel })
  }, [
    props.streamingSectionId,
    props.sections,
    resolveSectionDisplayTitle,
    t,
  ])

  return (
    <ComplianceDocumentView
      ref={ref}
      studioScopeId={props.complianceId}
      sections={props.sections}
      onSectionsChange={props.onSectionsChange}
      onAutosave={props.onAutosave}
      onCreateVersion={props.onCreateVersion}
      isAutosaving={props.isAutosaving}
      isCreatingVersion={props.isCreatingVersion}
      lastSavedAt={props.lastSavedAt}
      isGenerating={props.isGenerating}
      onExportPdf={props.onExportPdf}
      isExportPending={props.isExportPending}
      generateMenu={props.generateMenu}
      onRegenerateSection={props.onRegenerateSection}
      contentSyncKey={props.contentSyncKey}
      sectionDomIdPrefix='risk-assessment'
      translationKeyPrefix='riskAssessmentPage'
      i18nNamespace='machineryDirectiveCompliance'
      inputIdPrefix='risk-assessment'
      imageUploadScopeId={props.complianceId}
      shouldShowSectionEditor={shouldShowRiskAssessmentSectionEditor}
      streamingSectionId={props.streamingSectionId}
      onFocusedEditorChange={props.onFocusedEditorChange}
      bubbleMenuContent={props.bubbleMenuContent}
      bubbleMenuHidden={props.bubbleMenuHidden}
      rewritePanelOpen={props.rewritePanelOpen}
      outstandingCalloutNavigatorEnabled
      streamingPanel={
        <ComplianceDocumentStreamingPanel
          isOpen={props.isGenerating}
          statusLabel={streamingStatusLabel}
          streamingContent={props.streamingContent}
        />
      }
    />
  )
})
